<template>
  <div>
    <div class="intro-y flex flex-col sm:flex-row items-center mt-2">
      <h2 class="text-lg font-medium mr-auto">
        Loan Closure Review Delegation Board
      </h2>
    </div>
    <div class="intro-y grid grid-cols-7 mt-2">
      <div id="app-status-board" class="box px-2 py-2 mr-2 col-span-5">
        <div
          class="flex flex-col sm:flex-row sm:items-end xl:items-start mb-4 ml-4"
        >
          <div id="tabulator-html-filter-form" class="xl:flex sm:mr-auto">
            <div class="sm:flex items-center sm:mr-4 mt-2 xl:mt-0">
              <label class="w-3/4 flex-none xl:flex-initial mr-2"
                >Agent Name</label
              >
              <input
                id="tabulator-html-filter-value"
                v-model="agentNameForFilter"
                class="form-control w-full mt-2 sm:mt-0"
                placeholder="Search..."
                type="text"
                @keyup.enter="submitAgentFilter"
              />
            </div>
            <div class="mt-2 xl:mt-0">
              <button
                id="tabulator-html-filter-go"
                class="btn btn-success w-full sm:w-16"
                type="button"
                @click="submitAgentFilter"
              >
                Go
              </button>
              <button
                id="tabulator-html-filter-reset"
                class="btn btn-secondary w-full sm:w-16 mt-2 sm:mt-0 sm:ml-1"
                type="button"
                @click="clearAgentFilter"
              >
                Reset
              </button>
            </div>
          </div>
          <!-- BEGIN: Clear Selection Switcher-->
          <div
            v-if="selectedAssignedLoans.length > 0"
            class="flex mt-5 sm:mt-0"
          >
            <button
              class="btn btn-rounded bg-theme-15 w-1/2 sm:w-auto mr-2"
              @click="emptySelectedAssignedLoansBag"
            >
              <XCircleIcon class="w-4 h-4 mr-2" />
              Clear Assigned Selections
            </button>
            <button
              class="btn btn-rounded bg-theme-11 w-1/2 sm:w-auto mr-2"
              @click="unassignSelectedAssigned"
            >
              <UserXIcon class="w-4 h-4 mr-2" />
              Unassign
              {{ selectedAssignedLoans.length }} Selections
            </button>
          </div>
          <div class="flex mt-5 sm:mt-0">
            <button
              v-if="selectedAgent > 0"
              id="tabulator-print"
              class="btn btn-rounded bg-theme-15 w-1/2 sm:w-auto mr-2"
              @click="resetAgentSelection"
            >
              <XCircleIcon class="w-4 h-4 mr-2" />
              Clear Selection
            </button>
          </div>
          <!-- END: Clear Selection Switcher-->
        </div>
        <div class="overflow-y-scroll" style="max-height: 84vh">
          <table class="table lending-tbl border-collapse">
            <thead>
              <tr class="bg-theme-13">
                <th class="border dark:border-dark-5" rowspan="2">
                  Agents
                </th>
                <th class="border dark:border-dark-5" colspan="4">
                  Loan Applications
                </th>
              </tr>
              <tr class="bg-theme-13">
                <th class="border dark:border-dark-5">
                  Assigned
                </th>
              </tr>
            </thead>
            <tbody v-if="!fetchingLendingAgents">
              <tr
                v-for="(lendingAgent, lendingAgentKey) in lendingAgents"
                :key="lendingAgentKey"
                :class="{
                  'bg-green-100': selectedAgent === lendingAgent.id
                }"
              >
                <td class="border" @click="setSelectedAgent(lendingAgent.id)">
                  <div class="font-medium text-left">
                    <div class="ml-3" v-if="lendingAgent.logged_in_today">
                      {{ lendingAgent.fullname }} <br />
                      <div
                        class="text-theme-1"
                      >
                        <CheckCircleIcon class="w-3 h-3" />
                        Active
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  v-for="(invAssignment,
                  invAssignmentKey) in lendingAgent.involved_assessments_as_per_status"
                  :key="invAssignmentKey"
                  class="border brd-cl-assigned"
                >
                  <table
                    v-if="invAssignment.length > 0"
                    class="table ln-assess-tbl"
                  >
                    <thead>
                      <tr>
                        <th class="border dark:border-dark-5">
                          Name
                        </th>
                        <th class="border dark:border-dark-5">
                          Amount
                        </th>
                        <th class="border dark:border-dark-5">
                          Wages
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(singleAssignment,
                        singleAssignmentKey) in invAssignment"
                        :key="singleAssignmentKey"
                      >
                        <td
                          class="border"
                          @click="
                            event =>
                              toggleInsertionToUnAssignmentSelectionBag(
                                singleAssignment,
                                event
                              )
                          "
                        >
                          <CheckCircleIcon
                            v-if="
                              selectedAssignedLoans.includes(
                                singleAssignment.uuid
                              )
                            "
                            class="w-4 h-4 float-left text-theme-11"
                          />
                          {{ singleAssignment.customer.firstname }}
                          {{
                            singleAssignment.customer.middlename ??
                              singleAssignment.customer.middlename
                          }}
                          {{
                            singleAssignment.customer.lastname ??
                              singleAssignment.customer.lastname
                          }}
                        </td>
                        <td class="border">
                          {{ singleAssignment.loan.total_amount }}
                        </td>
                        <td class="border">
                          {{ singleAssignment.total_income }}

                          <a
                            class="ml-2 underline text-blue-600 hover:cursor-pointer"
                            target="_blank"
                            @click.prevent="
                              onClickResolveAppDetailsAndReRoute(
                                singleAssignment.application_id
                              )
                            "
                            >View</a
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
            <FetchingRecordTblTemplate v-else />
          </table>
        </div>
      </div>
      <div id="unassigned-app-list" class="box px-2 py-2 col-span-2">
        <div class="mb-2 grid grid-rows-2">
          <div>
            <div class="form-check">
              <div class="dark-mode-switcher">
                <div
                  :class="{
                    'dark-mode-switcher__toggle--active':
                      selectedUnassignedLoans.length === unassignedLoans.length
                  }"
                  class="dark-mode-switcher__toggle border"
                  @click="selectAllUnassigned"
                ></div>
              </div>
              <label class="form-check-label">
                Select All
              </label>
            </div>
          </div>
          <div>
            <button
              v-if="selectedUnassignedLoans.length > 0"
              class="btn btn-sm btn-rounded bg-theme-3 text-white w-auto mr-2"
              type="button"
              @click="assignSelectedUnassigned"
            >
              <CheckCircleIcon class="w-3 h-3 mr-1" />
              Assign
            </button>
            <button
              v-if="selectedUnassignedLoans.length > 0"
              class="btn btn-sm btn-rounded bg-theme-15 text-white w-auto mr-2"
              @click="emptySelectedUnassignedLoansBag"
            >
              <XCircleIcon class="w-3 h-3 mr-1" />
              Clear
            </button>
          </div>
        </div>
        <div class="overflow-y-scroll" style="max-height: 84vh;">
          <table class="table lending-tbl">
            <thead>
              <tr class="bg-theme-13">
                <th class="border dark:border-dark-5" colspan="3">
                  Unassigned Queue
                </th>
              </tr>
            </thead>
            <tbody v-if="!fetchingUnassignedLoans">
              <tr>
                <td class="border">
                  <table class="table ln-assess-tbl">
                    <thead>
                      <tr>
                        <th
                          v-if="selectedAgent"
                          class="border dark:border-dark-5"
                        ></th>
                        <th class="border dark:border-dark-5">
                          Name
                        </th>
                        <th class="border dark:border-dark-5">
                          Created at
                        </th>
                        <th class="border dark:border-dark-5">
                          Amount
                        </th>
                        <th class="border dark:border-dark-5">
                          Gross surplus
                        </th>
                        <th class="border dark:border-dark-5">
                          Wages
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(unLoan, unLoanKey) in unassignedLoans"
                        :key="unLoanKey"
                      >
                        <td
                          v-if="selectedAgent"
                          class="border-collapse bg-theme-5"
                          @click="
                            e =>
                              toggleInsertionToAssignmentSelectionBag(
                                unLoan.id,
                                e.shiftKey
                              )
                          "
                        >
                          <CircleIcon
                            v-if="!selectedUnassignedLoans.includes(unLoan.id)"
                            class="w-4 h-4"
                          />
                          <CheckCircleIcon
                            v-if="selectedUnassignedLoans.includes(unLoan.id)"
                            class="w-4 h-4"
                          />
                        </td>
                        <td
                          class="border"
                          @click="
                            e =>
                              toggleInsertionToAssignmentSelectionBag(
                                unLoan.id,
                                e.shiftKey
                              )
                          "
                        >
                          {{ unLoan.customer_name }}
                        </td>
                        <td class="border">{{ unLoan.created_at }}</td>
                        <td class="border">{{ unLoan.total_amount }}</td>
                        <td class="border">{{ unLoan.gross_surplus }}</td>

                        <td class="border">
                          {{ unLoan.total_income }}
                          <a
                            class="ml-2 underline text-blue-600 hover:cursor-pointer"
                            target="_blank"
                            @click.prevent="
                              onClickResolveAppDetailsAndReRoute(unLoan.id)
                            "
                            >View</a
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
            <FetchingRecordTblTemplate v-else />
          </table>
        </div>
      </div>
    </div>
    <FundoLmsToastAlert />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { lendingClosureReviewDelegationBoard } from "@/composables/lendingClosureReviewDelegationBoard";
import FundoLmsToastAlert from "@/components/fundo-lms-toast-alert/Main";
import FetchingRecordTblTemplate from "@/components/fetching-record-tbl-template/Main";

export default defineComponent({
  components: {
    FundoLmsToastAlert,
    FetchingRecordTblTemplate
  },
  setup() {
    const {
      adminUser,
      agentNameForFilter,
      clearAgentFilter,
      submitAgentFilter,
      fetchingLendingAgents,
      lendingAgents,
      unassignedLoans,
      fetchingUnassignedLoans,
      selectedUnassignedLoans,
      selectedAssignedLoans,
      toggleInsertionToAssignmentSelectionBag,
      toggleInsertionToUnAssignmentSelectionBag,
      removeFromSelectedUnassignedLoans,
      removeFromSelectedAssignedLoans,
      emptySelectedUnassignedLoansBag,
      emptySelectedAssignedLoansBag,
      selectAllUnassigned,
      assignSelectedUnassigned,
      unassignSelectedAssigned,
      selectedAgent,
      setSelectedAgent,
      resetAgentSelection,
      onClickResolveAppDetailsAndReRoute,
      busy,
      sideMenuCollapsed
    } = lendingClosureReviewDelegationBoard();

    return {
      adminUser,
      agentNameForFilter,
      clearAgentFilter,
      submitAgentFilter,
      fetchingLendingAgents,
      lendingAgents,
      unassignedLoans,
      fetchingUnassignedLoans,
      selectedUnassignedLoans,
      selectedAssignedLoans,
      toggleInsertionToAssignmentSelectionBag,
      toggleInsertionToUnAssignmentSelectionBag,
      removeFromSelectedUnassignedLoans,
      removeFromSelectedAssignedLoans,
      emptySelectedUnassignedLoansBag,
      emptySelectedAssignedLoansBag,
      selectAllUnassigned,
      assignSelectedUnassigned,
      unassignSelectedAssigned,
      selectedAgent,
      setSelectedAgent,
      resetAgentSelection,
      onClickResolveAppDetailsAndReRoute,
      busy,
      sideMenuCollapsed
    };
  }
});
</script>
<style>
.lending-tbl td {
  padding: 0 !important;
  text-align: center;
  font-size: smaller;
}

.lending-tbl th {
  padding: 0 !important;
  text-align: center;
  font-size: smaller;
}

.ln-assess-tbl {
  border: 2px solid #1e2533;
}

.ln-assess-tbl th {
  background: #9fa8bb;
}
</style>
